import dynamic from "next/dynamic";
import SpeckleTitle from "../../SpeckleTitle";
import SpeckleBreadcrumbs, {
  SpeckleCrumb,
  SpeckleCrumbSeparator,
} from "../../SpeckleBreadcrumbs";
import ParticleLoading from "components/animations/particle-loading.json";
import { useWizard } from "components/elements/Wizard/useWizard";
import { speckleValidateAccount } from "components/modules/UploadDialog/lib/actions/speckleValidateAccount";
import { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { SpeckleImportModel } from "../../types/SpeckleImportModel";
import Loading from "components/elements/Loading/Loading";

const LottiePlayer = dynamic(
  () => import("components/animations/LottiePlayer"),
  {
    ssr: false,
  },
);

export default function SpeckleAccountValidationStepVariant(p: {
  onValidationSuccess: () => void;
  connectedAccountId: string | undefined;
}) {
  const { stepViewVariant } = useWizard();
  const returnToAccounts = () => stepViewVariant(null);
  const [isValidating, setIsValidating] = useState(true);
  const { values } = useFormikContext<SpeckleImportModel>();

  useEffect(() => {
    async function validateAccount() {
      if (p.connectedAccountId == undefined) {
        stepViewVariant("");
        return;
      }

      const isValid = await speckleValidateAccount(p.connectedAccountId);

      if (isValid) {
        p.onValidationSuccess();
      } else {
        setIsValidating(false);
      }
    }

    validateAccount();
  }, [p.connectedAccountId, p.onValidationSuccess]);

  return (
    <div>
      <div className="grid grid-cols-2 gap-8">
        <header>
          <SpeckleTitle />
        </header>
        <div className="space-y-5">
          <SpeckleBreadcrumbs>
            <SpeckleCrumb onClick={returnToAccounts}>Accounts</SpeckleCrumb>
            <SpeckleCrumbSeparator />
            <SpeckleCrumb active>{values.connectedAccountId}</SpeckleCrumb>
          </SpeckleBreadcrumbs>
        </div>
      </div>
      <div className="h-full select-none space-y-8 overflow-y-auto pb-8 pr-4">
        <div className="mb-10 px-20 pt-20">
          {isValidating ? (
            <div className="mt-40">
              <Loading />
            </div>
          ) : (
            <div className="flex w-full flex-col items-center">
              <LottiePlayer
                lottieAnimation={ParticleLoading}
                className="mb-8 mt-10 w-40"
              />
              <div className="w-2/3 space-y-1 text-center">
                <p className="text-2xl font-light">Account Session Expired</p>
                <p className="pb-10 text-lg font-thin">
                  It looks like the session for this account has expired. You
                  will need to sign in to this account again to continue.
                </p>
                <button
                  type="button"
                  className="rounded-lg bg-black p-4 px-32 font-thin text-white hover:bg-gray-800 focus:ring-offset-2"
                  onClick={() => stepViewVariant("add-account")}
                >
                  Sign In
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
